/* MXJ: collection and search pagination enhancement, kept separate from Dawn's core scripts. */
if (!customElements.get('mxj-infinite-scroll')) {
  customElements.define('mxj-infinite-scroll', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !('IntersectionObserver' in window)) return;
      this.gridSelector = this.dataset.gridSelector || '#product-grid';
      this.grid = this.parentElement.querySelector(this.gridSelector);
      this.fallback = this.querySelector('[data-infinite-pagination]');
      this.controls = this.querySelector('[data-infinite-controls]');
      this.button = this.querySelector('button');
      this.status = this.querySelector('[role="status"]');
      if (!this.grid || !this.fallback || !this.controls || !this.button || !this.status) return;
      this.initialized = true;
      const french = document.documentElement.lang.toLowerCase().startsWith('fr');
      this.labels = french
        ? { more: 'Afficher plus de produits', loading: 'Chargement…', error: 'Le chargement a échoué. Réessayez ou utilisez les pages ci-dessous.', retry: 'Réessayer', products: 'produits affichés', end: 'Tous les produits sont affichés.' }
        : { more: 'Load more products', loading: 'Loading…', error: 'Products could not load. Try again or use the page links below.', retry: 'Try again', products: 'products shown', end: 'All products are shown.' };
      if (this.dataset.resultType === 'search') {
        Object.assign(this.labels, french
          ? { more: 'Afficher plus de résultats', products: 'résultats affichés', end: 'Tous les résultats sont affichés.' }
          : { more: 'Load more results', products: 'results shown', end: 'All results are shown.' });
      }
      this.next = this.dataset.nextUrl;
      this.visited = new Set();
      this.onClick = (event) => this.load(event.detail === 0);
      this.button.addEventListener('click', this.onClick);
      this.fallback.classList.add('hidden');
      this.controls.classList.remove('hidden');
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      }, { rootMargin: '0px 0px 400px 0px' });
      this.render();
      if (this.next) this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.button?.removeEventListener('click', this.onClick);
    }

    productKey(item) {
      if (item.dataset.resultKey) return item.dataset.resultKey;
      const href = item.querySelector('a[href*="/products/"]')?.getAttribute('href');
      return href ? href.split('/products/').pop().split(/[?#]/)[0] : null;
    }

    render() {
      const count = this.grid.children.length;
      this.status.textContent = `${count} / ${this.dataset.total} ${this.labels.products}`;
      if (!this.next && count >= Number(this.dataset.total)) this.status.textContent += ` — ${this.labels.end}`;
      this.button.textContent = this.labels.more;
      this.button.classList.toggle('hidden', !this.next);
      this.button.disabled = false;
    }

    async load(focusNewProduct = false) {
      if (this.loading || !this.next || !this.isConnected || this.grid.closest('.collection.loading')) return;
      this.loading = true;
      this.button.disabled = true;
      this.button.classList.add('hidden');
      this.status.textContent = this.labels.loading;
      this.grid.setAttribute('aria-busy', 'true');
      this.controller = new AbortController();
      let succeeded = false;
      try {
        const url = new URL(this.next, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Unexpected pagination origin');
        url.searchParams.set('section_id', this.dataset.sectionId);
        if (this.visited.has(url.href)) throw new Error('Repeated pagination URL');
        const response = await fetch(url.href, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`Pagination response ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = html.querySelector(this.gridSelector);
        const nextControl = html.querySelector('mxj-infinite-scroll');
        const nextFallback = nextControl?.querySelector('[data-infinite-pagination]');
        if (!nextGrid || !nextControl || !nextFallback || nextGrid.closest('[data-id]')?.dataset.id !== this.grid.closest('[data-id]')?.dataset.id) {
          throw new Error('Incomplete grid response');
        }
        if (!this.isConnected || !this.grid.isConnected) return;
        const existing = new Set(Array.from(this.grid.children, (item) => this.productKey(item)));
        const items = Array.from(nextGrid.children).filter((item) => {
          const key = this.productKey(item);
          if (!key || existing.has(key)) return false;
          existing.add(key);
          return true;
        });
        if (!items.length) throw new Error('No new products returned');
        items.forEach((item) => item.classList.add('scroll-trigger--cancel'));
        this.grid.append(...items);
        this.fallback.replaceChildren(...Array.from(nextFallback.childNodes));
        this.next = nextControl.dataset.nextUrl;
        this.dataset.nextUrl = this.next || '';
        this.visited.add(url.href);
        this.fallback.classList.add('hidden');
        this.render();
        if (this.next) this.observer.observe(this);
        else this.observer.disconnect();
        if (focusNewProduct) items[0].querySelector('a[href]')?.focus({ preventScroll: true });
        succeeded = true;
      } catch (error) {
        if (error.name === 'AbortError' || !this.isConnected) return;
        this.observer.disconnect();
        this.status.textContent = this.labels.error;
        this.button.textContent = this.labels.retry;
        this.button.classList.remove('hidden');
        this.button.disabled = false;
        this.fallback.classList.remove('hidden');
      } finally {
        this.loading = false;
        this.grid.removeAttribute('aria-busy');
        if (succeeded && this.next) requestAnimationFrame(() => {
          const bounds = this.getBoundingClientRect();
          if (bounds.top < window.innerHeight + 400 && bounds.bottom > 0) this.load();
        });
      }
    }
  });
}
